import { useState, useRef, useEffect } from 'react';

// Constants for carousel behavior
const SWIPE_THRESHOLD = 50; // pixels required to trigger swipe

/**
 * Hook personnalisé pour gérer un carrousel de photos
 * Gère l'état, la navigation, les gestes tactiles et le clavier
 */
export const usePhotoCarousel = () => {
    const [carouselOpen, setCarouselOpen] = useState(false);
    const [carouselIndex, setCarouselIndex] = useState(0);
    const [carouselPhotos, setCarouselPhotos] = useState([]);
    const carouselRef = useRef(null);
    
    // Pour le swipe mobile
    const touchStartX = useRef(null);
    const touchEndX = useRef(null);
    
    // Pour sauvegarder la position de scroll
    const scrollPosition = useRef(0);

    // Gestion du swipe tactile pour mobile
    const handleTouchStart = (e) => {
        touchStartX.current = e.touches[0].clientX;
    };

    const handleTouchMove = (e) => {
        touchEndX.current = e.touches[0].clientX;
    };

    const handleTouchEnd = () => {
        if (touchStartX.current !== null && touchEndX.current !== null) {
            const delta = touchStartX.current - touchEndX.current;
            if (Math.abs(delta) > SWIPE_THRESHOLD) {
                if (delta > 0) {
                    nextPhoto(); // swipe gauche → photo suivante
                } else {
                    prevPhoto(); // swipe droite → photo précédente
                }
            }
        }
        touchStartX.current = null;
        touchEndX.current = null;
    };

    // Ouvre le carrousel à l'index donné
    const openCarousel = (index, photos) => {
        setCarouselPhotos(photos);
        setCarouselIndex(index);
        setCarouselOpen(true);
    };

    // Ferme le carrousel
    const closeCarousel = () => {
        setCarouselOpen(false);
    };

    // Navigation carrousel
    const prevPhoto = () => {
        const length = carouselPhotos?.length || 1;
        setCarouselIndex((prev) => (prev === 0 ? length - 1 : prev - 1));
    };
    
    const nextPhoto = () => {
        const length = carouselPhotos?.length || 1;
        setCarouselIndex((prev) => (prev === length - 1 ? 0 : prev + 1));
    };

    // Gestion des touches clavier
    useEffect(() => {
        const handleKeyPress = (e) => {
            if (!carouselOpen) return;
            
            switch(e.key) {
                case 'ArrowLeft':
                    e.preventDefault();
                    prevPhoto();
                    break;
                case 'ArrowRight':
                    e.preventDefault();
                    nextPhoto();
                    break;
                case 'Escape':
                    e.preventDefault();
                    closeCarousel();
                    break;
                default:
                    break;
            }
        };
        
        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [carouselOpen, carouselPhotos?.length]);

    // Bloquer le scroll de la page quand le carousel est ouvert
    useEffect(() => {
        if (carouselOpen) {
            // Sauvegarder la position actuelle
            scrollPosition.current = window.scrollY;
            document.body.style.overflow = 'hidden';
            document.body.style.position = 'fixed';
            document.body.style.top = `-${scrollPosition.current}px`;
            document.body.style.width = '100%';
        } else if (scrollPosition.current > 0) {
            // Restaurer le scroll seulement si on avait une position sauvegardée
            const savedPosition = scrollPosition.current;
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.width = '';
            window.scrollTo(0, savedPosition);
            scrollPosition.current = 0;
        }

        // Cleanup au unmount
        return () => {
            if (document.body.style.position === 'fixed') {
                const savedPosition = scrollPosition.current;
                document.body.style.overflow = '';
                document.body.style.position = '';
                document.body.style.top = '';
                document.body.style.width = '';
                if (savedPosition > 0) {
                    window.scrollTo(0, savedPosition);
                }
            }
        };
    }, [carouselOpen]);

    return {
        carouselOpen,
        carouselIndex,
        carouselPhotos,
        carouselRef,
        handleTouchStart,
        handleTouchMove,
        handleTouchEnd,
        openCarousel,
        closeCarousel,
        prevPhoto,
        nextPhoto,
        setCarouselPhotos,
        setCarouselIndex,
    };
};
